/**
 * Therapeutic Area Manager Component
 *
 * Admin interface for managing therapeutic areas (MedDRA-based).
 */

import React, { useState } from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { TherapeuticArea } from '../../types';

interface TherapeuticAreaManagerProps {
  therapeuticAreas: TherapeuticArea[];
  onUpdate: (areas: TherapeuticArea[]) => Promise<void>;
}

const TherapeuticAreaManager: React.FC<TherapeuticAreaManagerProps> = ({
  therapeuticAreas,
  onUpdate,
}) => {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Partial<TherapeuticArea>>({});
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newArea, setNewArea] = useState<Partial<TherapeuticArea>>({
    code: '',
    name: '',
    description: '',
  });
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const handleEdit = (area: TherapeuticArea) => {
    setEditingId(area.code);
    setEditForm(area);
    setError(null);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditForm({});
    setError(null);
  };

  const handleSaveEdit = async () => {
    if (!editForm.code || !editForm.name) {
      setError('Code and name are required');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const updatedAreas = therapeuticAreas.map((area) =>
        area.code === editingId ? (editForm as TherapeuticArea) : area
      );
      await onUpdate(updatedAreas);
      setEditingId(null);
      setEditForm({});
    } catch (err: any) {
      setError(err.message || 'Failed to update therapeutic area');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (code: string) => {
    if (!window.confirm('Delete this therapeutic area? This may affect linked indications.')) {
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const updatedAreas = therapeuticAreas.filter((area) => area.code !== code);
      await onUpdate(updatedAreas);
    } catch (err: any) {
      setError(err.message || 'Failed to delete therapeutic area');
    } finally {
      setSaving(false);
    }
  };

  const handleAdd = async () => {
    if (!newArea.code?.trim() || !newArea.name?.trim()) {
      setError('Code and name are required');
      return;
    }

    // Check for duplicate code
    if (therapeuticAreas.some((area) => area.code === newArea.code)) {
      setError('Code already exists');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const updatedAreas = [...therapeuticAreas, newArea as TherapeuticArea];
      await onUpdate(updatedAreas);
      setNewArea({ code: '', name: '', description: '' });
      setAddDialogOpen(false);
    } catch (err: any) {
      setError(err.message || 'Failed to add therapeutic area');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAddDialogOpen(true)}
            disabled={saving}
          >
            Add Therapeutic Area
          </Button>
        </Box>
        <Box>
          <Alert severity="info" sx={{ py: 0.5 }}>
            {therapeuticAreas.length} therapeutic areas
          </Alert>
        </Box>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell width="15%">Code</TableCell>
              <TableCell width="25%">Name</TableCell>
              <TableCell width="45%">Description</TableCell>
              <TableCell width="15%" align="right">
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {therapeuticAreas.map((area) => (
              <TableRow key={area.code}>
                {editingId === area.code ? (
                  <>
                    <TableCell>
                      <TextField
                        size="small"
                        fullWidth
                        value={editForm.code || ''}
                        onChange={(e) =>
                          setEditForm((prev) => ({ ...prev, code: e.target.value }))
                        }
                        disabled
                        helperText="Code cannot be changed"
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        fullWidth
                        value={editForm.name || ''}
                        onChange={(e) =>
                          setEditForm((prev) => ({ ...prev, name: e.target.value }))
                        }
                        disabled={saving}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        fullWidth
                        multiline
                        rows={2}
                        value={editForm.description || ''}
                        onChange={(e) =>
                          setEditForm((prev) => ({ ...prev, description: e.target.value }))
                        }
                        disabled={saving}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Save">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={handleSaveEdit}
                          disabled={saving}
                        >
                          <SaveIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Cancel">
                        <IconButton
                          size="small"
                          onClick={handleCancelEdit}
                          disabled={saving}
                        >
                          <CancelIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </>
                ) : (
                  <>
                    <TableCell>{area.code}</TableCell>
                    <TableCell>
                      <strong>{area.name}</strong>
                    </TableCell>
                    <TableCell>{area.description || '—'}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => handleEdit(area)}
                          disabled={saving || editingId !== null}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDelete(area.code)}
                          disabled={saving || editingId !== null}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add Dialog */}
      <Dialog open={addDialogOpen} onClose={() => !saving && setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Therapeutic Area</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Code"
              placeholder="e.g., TA011"
              value={newArea.code}
              onChange={(e) => setNewArea((prev) => ({ ...prev, code: e.target.value }))}
              required
              helperText="Unique identifier (e.g., TA001, TA002)"
              disabled={saving}
            />
            <TextField
              label="Name"
              placeholder="e.g., Dermatology"
              value={newArea.name}
              onChange={(e) => setNewArea((prev) => ({ ...prev, name: e.target.value }))}
              required
              disabled={saving}
            />
            <TextField
              label="Description"
              placeholder="Optional description"
              value={newArea.description}
              onChange={(e) =>
                setNewArea((prev) => ({ ...prev, description: e.target.value }))
              }
              multiline
              rows={3}
              disabled={saving}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleAdd} disabled={saving}>
            {saving ? 'Adding...' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TherapeuticAreaManager;
